import { Box, Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton } from "@mui/material"
import { CloseOutlined } from "@mui-symbols-material/w300"
import { useParams } from "@tanstack/react-router"
import { type FC, useState } from "react"
import { useFormContext } from "react-hook-form"

import { COMPANY_EDIT_ROUTE } from "../../constants"

import { AddContactLocationRolesForm } from "./AddContactLocationRolesForm"
import { AddContactMethodsForm } from "./AddContactMethodsForm"

import { Button } from "@/components/common/Button"
import { ContactGeneralInfoForm } from "@/components/Contact/ContactGeneralnfoForm"
import type { ContactAddFormValues } from "@/schemas/contactFormSchema"
import { ContactGeneralInfoFormSchema } from "@/schemas/contactFormSchema"
import type { DialogCloseAction } from "@/types/common"

interface ContactModalProps {
  open: boolean
  onClose: (action: DialogCloseAction) => void
  onSubmit: (data: ContactAddFormValues) => void
}

const AddContactDialog: FC<ContactModalProps> = ({ open, onClose, onSubmit }) => {
  const { companyId } = useParams({ from: COMPANY_EDIT_ROUTE })
  const { reset, handleSubmit, getValues, formState } = useFormContext<ContactAddFormValues>()
  const [step, setStep] = useState(0)

  const isGeneralInfoValid = ContactGeneralInfoFormSchema.safeParse(getValues()).success
  const isFormValid = formState.isValid

  const onNextStep = async () => {
    if (isFormValid) setStep((prevStep) => prevStep + 1)
  }

  const handleFormSubmit = (value: ContactAddFormValues) => {
    onSubmit(value)
    onClose("success")
    setStep(0)
  }

  const handleResetClick = () => {
    reset()
    onClose("cancel")
    setStep(0)
  }

  const handleDialogClose = (_: React.SyntheticEvent, reason: DialogCloseAction) => {
    reset()
    setStep(0)
    onClose(reason)
  }

  const renderStepContent = () => {
    switch (step) {
      case 0:
        return <ContactGeneralInfoForm companyId={companyId} />
      case 1:
        return <AddContactMethodsForm companyId={companyId} />
      case 2:
        return <AddContactLocationRolesForm companyId={companyId} />
      default:
        return null
    }
  }

  const renderStepActions = () => {
    switch (step) {
      case 0:
        return (
          <Button onClick={onNextStep} disabled={!isGeneralInfoValid}>
            Continue
          </Button>
        )
      case 1:
        return (
          <Box>
            <Button
              variant='primary'
              appearance='text'
              className='mr-6'
              onClick={handleSubmit(handleFormSubmit)}
              disabled={!isFormValid}
            >
              Save And Exit
            </Button>
            <Button onClick={onNextStep} disabled={!isFormValid}>
              Continue
            </Button>
          </Box>
        )
      case 2:
        return (
          <Box>
            <Button
              variant='primary'
              appearance='text'
              className='mr-6'
              onClick={handleSubmit(handleFormSubmit)}
              disabled={!isFormValid}
            >
              Save And Exit
            </Button>
            <Button onClick={handleSubmit(handleFormSubmit)} disabled={!isFormValid}>
              Save
            </Button>
          </Box>
        )
      default:
        return null
    }
  }

  return (
    <Dialog open={open} onClose={handleDialogClose} fullWidth>
      <DialogTitle className='pl-10 font-normal text-primary'>Add Contact</DialogTitle>
      <IconButton aria-label='close' onClick={() => onClose("cancel")} className='absolute right-4 top-3 text-primary'>
        <CloseOutlined />
      </IconButton>
      <Divider />
      <DialogContent classes={{ root: "p-10" }}>{renderStepContent()}</DialogContent>
      <DialogActions className='justify-between px-10 pb-10'>
        <Button appearance='outlined' onClick={handleResetClick}>
          Cancel
        </Button>
        {renderStepActions()}
      </DialogActions>
    </Dialog>
  )
}

export default AddContactDialog
